import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { EventsManager, ReelsEvents, WinEvents } from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { TimelineLabels } from "../../../abstracts/types/enums";
import { getRandomBetween } from "../../../abstracts/utils/JSUtils";
import { winLevels } from "../../UCConfig";
import { UgliestCatch } from "../../UgliestCatch";
import UCReel from "../../modules/Reels/UCReel";
import { UCWinView } from "../../modules/Winnings/UCWinView";
import { totalWinDecorator } from "../decorators/UCTotalWinDecorators";

export class UCNormalWinPresentation extends AbstractFlowPresentation {
    protected ucReels: UCReel[];
    protected winView: UCWinView;
    protected noWinCounter: number = 0;
    protected currentNoWinLimit: number = 4;

    constructor(name: string = `UCNormalWinPresentation`) {
        super(name);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (winView: UCWinView) => {
            this.winView = winView;
        });

        EventsManager.on(ReelsEvents.GET_REELS, (reels: AbstractReel[]) => {
            this.ucReels = reels as UCReel[];
        });
    }

    protected shouldFinishPresentation(): boolean {
        return this.model.gameData.accumulatedRoundWin <= 0;
    }

    public start(flowStateController: FlowStateController) {
        super.start(flowStateController);
        if (
            UgliestCatch.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame &&
            UgliestCatch.model.gameData.betWayWins.length > 0
        ) {
            const tl = gsap.timeline();
            tl.add(
                UgliestCatch.getIndiSymWinHighlightTl(
                    UgliestCatch.model.gameData.betWayWins,
                    this.ucReels
                )
            );
            tl.add(() => {
                this.getRegularFlow();
            });
        } else {
            this.getRegularFlow();
        }
    }

    private getRegularFlow(): void {
        if (this.shouldFinishPresentation()) {
            this.addSpecialAnimations();

            return;
        }

        this.handleWinnings();
    }

    protected addSpecialAnimations() {
        super.addSpecialAnimations();

        if (this.model.data.nextMode === this.model.data.mode) {
            this.handleNoWin();
        } else {
            this.finishPresentation();
        }
    }

    protected handleNoWin() {
        this.noWinCounter++;

        if (this.noWinCounter >= this.currentNoWinLimit) {
            this.noWinCounter = 0;
            this.currentNoWinLimit = getRandomBetween(4, 8, true);

            const noWinScreenAnimation = this.winView.showNoWinScreen();

            this.flowPresentationTimeline.call(() => {
                this.controller.setSkipCallback(() => {
                    noWinScreenAnimation.timeScale(3);
                });
            });
            this.flowPresentationTimeline.add(noWinScreenAnimation);
            this.flowPresentationTimeline.resume();
        } else {
            this.finishPresentation();
        }
    }

    protected handleWinnings() {
        const isWinBelowStake: boolean = UgliestCatch.model.isWinBelowStake();

        if (this.model.data.wasFeatureBuy || isWinBelowStake) {
            SlotGame.keypadPlugin.setWin(
                this.model.gameData.totalSpinWinnings,
                true,
                isWinBelowStake,
                true
            );
            this.flowPresentationTimeline.resume();
            return;
        }

        totalWinDecorator({
            winLevels: winLevels,
            winAmount: this.model.gameData.accumulatedRoundWin,
            timeline: this.flowPresentationTimeline,
            winScreen: this.winView.totalWinScreen,
            betValue: this.model.gameData.playedBetValue,
            flowStateController: this.controller,
            winIsBelowStake: isWinBelowStake,
            reels: this.ucReels,
            winSymbolsPos: UgliestCatch.model.finalWinningPosition,
            betWayWins: this.model.gameData.betWayWins,
            currentGameMode: this.model.gameData.currentMode,
            nextGameMode: this.model.gameData.nextMode,
            keypadPlugin: this.keypad,
            finishSpineAnimationDuration: 5,
        });

        this.flowPresentationTimeline.call(
            () => {
                if (this.model.gameData.brokeTheBank) {
                    SlotGame.keypadPlugin.setWin(this.model.gameData.totalSpinWinnings, true);
                } else {
                    SlotGame.keypadPlugin.setWin(
                        this.model.gameData.accumulatedRoundWin,
                        this.model.gameData.nextMode === AbstractGameMode.NORMAL,
                        isWinBelowStake,
                        false
                    );
                }
            },
            [],
            TimelineLabels.TEXT_COUNT_COMPLETE
        );

        this.flowPresentationTimeline.resume();

        this.noWinCounter = 0;
    }

    protected finishPresentation() {
        const delay =
            this.model.gameData.nextMode !== AbstractGameMode.NORMAL &&
            this.model.gameData.nextMode !== this.model.gameData.mode
                ? 0.4
                : 0;

        gsap.delayedCall(delay, () => {
            super.finishPresentation();
        });
    }
}
